/**
 * "My Learning" — ONE library holding every learning path and every course the learner is in.
 *
 * ------------------------------------------------------------------ the prototype ----
 * This is the prototype's `section.dash__library.mylearning`
 * (`index.html:5164-5402`, page spec `specs/pages/03-dashboard.md`). What it does, exactly:
 *
 *   · one heading, "My Learning", with the sub-line "Everything you're enrolled in, in one
 *     place." and two CTAs — find a new course, find a new learning path
 *   · one chip row: All / Learning Paths / Courses / In progress / Completed, each with a
 *     live count, filtering by `data-ml-type` and `data-ml-state` (`index.html:11549`)
 *   · one grid, `.prog-grid.mylearning__grid`, in which BOTH entities render as the SAME
 *     `.prog-card`: type badge on the art, an eyebrow code, a progress row, a progress bar,
 *     a meta row, and a foot carrying a next-step line plus a Continue button
 *   · THE PATH CARDS COME FIRST. Both of them, before any course.
 *
 * An earlier build of this dashboard put paths in a separate section above the courses,
 * because a path rendered as the catalogue card (`.course-card.path-card`) and a course
 * rendered as `.enrolled-card`, and two card shapes in one grid look like a bug. That
 * reasoning was sound and the conclusion was still wrong: the prototype's answer is not to
 * split the section, it is to make the two things the same shape. So the shape is unified
 * here and the split is gone.
 *
 * ------------------------------------------------------- what it will NOT do ---------
 * NOTHING HERE INVENTS PROGRESS. Every number on every card is measured:
 *
 *   a course   `myCourseProgress` — the platform's own completion summary for this learner
 *   a path     the weighted completion of its steps, each measured the same way
 *
 * A path somebody has joined and not started reads "Not started" and draws NO BAR, because a
 * 0% bar is a picture of falling behind and there is nothing yet to have fallen behind on.
 * The same is true of a course. Neither case is a placeholder: it is the real answer.
 *
 * WHAT IS IN THE LIBRARY is what the learner is IN — active course enrolments, and paths with
 * an ACTIVE path enrolment. Not the catalogue. A path the academy publishes and this learner
 * has not joined belongs on /paths, and putting it here would make "My Learning" a shop.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../shared/ui';
import { text } from '../../shared/lib/config';
import { learnHref, myCourseProgress, type LearnerCourse } from '../../shared/lib/catalogue';
import { pathHref, pct, type LearningPathView, type PathStepView } from '../../shared/lib/paths';
import { pathwayPriceView, type PricingSeam } from '../../shared/lib/pricing';
import { usePathwayPrices } from '../../shared/lib/usePricing';
import './learner.css';
import './paths.css';
import './my-learning.css';

/* ==================================================================== the item ===== */

/** Where an item sits, in the two words the chips filter on. */
type ItemState = 'not_started' | 'in_progress' | 'completed' | 'archived';

/**
 * One row of the library, whichever kind of thing it is.
 *
 * A single normalised shape rather than two card components with a shared stylesheet: the
 * grid sorts, counts and filters over a mixed list, and every one of those operations would
 * otherwise need to know which of two objects it was holding.
 */
interface LibraryItem {
  id: string;
  kind: 'path' | 'course';
  title: string;
  /** Where the card's title and its button both lead. */
  href: string;
  image: string | null;
  /** Eyebrow above the title — the entity's own code, as the prototype shows one. */
  code: string | null;
  /** The publisher chip on the art, when the platform knows one. */
  org: string | null;
  description: string | null;
  state: ItemState;
  /** 0..1, or null when nothing has been measured. NEVER a stand-in value. */
  progress: number | null;
  /** The right-hand cell of the progress row: "2 of 5 courses". Paths only. */
  progressDetail: string | null;
  /** Facts under the body: step count, order rule, course code. */
  meta: { icon: string; label: string }[];
  /** The card's last line — the prototype's `Next: <course>` / "Resume where you left off". */
  foot: { label: string | null; value: string; done?: boolean };
  action: string;
  /** A second, external destination: the platform renders certificates, we do not. */
  certificateHref?: string | null;
  strip?: { tone?: 'success'; icon: string; text: string };
}

/* ===================================================================== chips ====== */

type Filter = 'all' | 'paths' | 'courses' | 'progress' | 'completed';

const CHIPS: { id: Exclude<Filter, 'all'>; label: string; match: (item: LibraryItem) => boolean }[] =
  [
    { id: 'paths', label: 'Learning Paths', match: (i) => i.kind === 'path' },
    { id: 'courses', label: 'Courses', match: (i) => i.kind === 'course' },
    { id: 'progress', label: 'In progress', match: (i) => i.state === 'in_progress' },
    { id: 'completed', label: 'Completed', match: (i) => i.state === 'completed' },
  ];

/* ================================================================= the section ===== */

export default function MyLearning({
  courses,
  paths,
  pathsPending,
}: {
  courses: LearnerCourse[];
  paths: LearningPathView[];
  /** Paths still loading. The grid waits rather than rendering courses and reflowing. */
  pathsPending: boolean;
}) {
  const [filter, setFilter] = useState<Filter>('all');

  // Progress is not in `/api/learner_home/init`, so a bar on a course card needs a second
  // source. It used to be one request PER CARD, fanned out with `useQueries`; it is one
  // request for the whole grid now — see `myCourseProgress`. The query is not keyed on the
  // course list, because the server scopes it from the session and the Host, so it runs in
  // parallel with the enrolments rather than after them.
  //
  // `myCourseProgress` never rejects, so there is no error branch to render: a course
  // missing from the map is indistinguishable from an untouched one, which is the truth of
  // it and is exactly what the per-course version did with a 403.
  const progress = useQuery({
    queryKey: ['my-course-progress'],
    queryFn: myCourseProgress,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const measured = progress.data ?? new Map<string, number>();

  // Built on every render rather than memoised, deliberately: the inputs are a handful of
  // enrolments and an array of query results whose identity changes each time anyway, so a
  // `useMemo` here would be a dependency list that lies rather than a saving.
  //
  // PATHS FIRST, which is the prototype's order and not an alphabetical accident: a path is
  // the layer that contains the courses under it, so a learner who is in one is following the
  // path rather than the courses it is made of.
  /**
   * NO PRICE IN THE LIBRARY, AND ONE EXCEPTION THAT IS NOT A PRICE (spec R6.6).
   *
   * A library shows what the learner OWNS, so nothing here states a cost: ownership is the
   * fact, not the price, and a tenant losing its Stripe must not change what a learner
   * already holds. The seam is read for exactly one reason — to tell a PAID path from a free
   * one — and no amount from it reaches the screen.
   *
   * That distinction is what stops the strip below being a false alarm on every card. A
   * joined path whose member courses are not enrolled is the NORMAL state of a free path in
   * this build, because path enrolment does not cascade into member courses; on a PAID path
   * it means the purchase was fulfilled for the path and not yet for its courses, which is
   * the one state in this grid a learner needs told about (AC-021).
   */
  const mine = paths.filter(isMine);
  const pathPrices = usePathwayPrices(mine.map((view) => view.path.key));

  /** The runs this learner is actually enrolled in — the same list the course cards use. */
  const enrolled = new Set(courses.map((entry) => entry.courseRun.courseId));

  const items: LibraryItem[] = [
    ...mine
      .sort(byPathAttention)
      .map((view) => fromPath(view, pendingGrants(view, pathPrices, enrolled))),
    ...courses.map((entry) => fromCourse(entry, measured)).sort(byCourseAttention),
  ];

  const counts: Record<Filter, number> = {
    all: items.length,
    paths: items.filter((i) => i.kind === 'path').length,
    courses: items.filter((i) => i.kind === 'course').length,
    progress: items.filter((i) => i.state === 'in_progress').length,
    completed: items.filter((i) => i.state === 'completed').length,
  };

  /*
   * A CHIP THAT CHANGES NOTHING IS NOT A CONTROL, IT IS A LABEL.
   *
   * The prototype's learner is in seven things and every chip partitions them. This academy's
   * learner may be in three courses and no path, where "Learning Paths (0)" filters to an
   * empty grid and "Courses (3)" filters to what is already on screen — two controls that
   * teach the reader the page is broken. So a chip appears only when it selects SOME of the
   * library and not ALL of it, and the row disappears entirely when none of them does.
   */
  const visibleChips = CHIPS.filter((chip) => counts[chip.id] > 0 && counts[chip.id] < items.length);
  const showChips = visibleChips.length > 0;
  const active = showChips ? filter : 'all';

  const shown =
    active === 'all' ? items : items.filter(CHIPS.find((c) => c.id === active)?.match ?? (() => true));

  return (
    <section className="dash__library mylearning" aria-labelledby="myLearningHeading">
      <div className="dash__head">
        <div>
          <h2 id="myLearningHeading">My learning</h2>
          <p>
            {items.length === 0
              ? 'You are not enrolled in anything yet.'
              : "Everything you're enrolled in, in one place."}
          </p>
        </div>
        <div className="mylearning__head-actions">
          <Link className="ui-btn" data-variant="secondary" to="/discover">
            <Icon name="add" />
            Find a new course
          </Link>
          {/* Only when the academy has one to find. The prototype shows both CTAs because
              its academy publishes paths; an academy that publishes none would be offering
              a door onto an empty room. */}
          {paths.length > 0 && (
            <Link className="ui-btn" data-variant="secondary" to="/paths">
              <Icon name="add" />
              Find a new {text('nav_path_singular', 'learning path').toLowerCase()}
            </Link>
          )}
        </div>
      </div>

      {pathsPending ? (
        /* Skeletons rather than a spinner, and rather than rendering the courses now and the
           paths a moment later: paths sort ABOVE courses, so arriving late would push the
           whole grid down under a reader who had started to read it. */
        <div className="prog-grid mylearning__grid" aria-hidden="true">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : items.length === 0 ? (
        <div className="mylearning__empty">
          <Icon name="school" />
          <h3>Nothing here yet</h3>
          <p>
            Courses and learning paths you join appear here, with your progress in each one.
            The catalogue is the place to start.
          </p>
          <Link className="ui-btn" data-variant="primary" to="/discover">
            Browse courses
          </Link>
        </div>
      ) : (
        <>
          {showChips && (
            <div className="mylearning__filters" role="group" aria-label="Filter my learning">
              <FilterChip
                id="all"
                label="All"
                count={counts.all}
                active={active === 'all'}
                onSelect={setFilter}
              />
              {visibleChips.map((chip) => (
                <FilterChip
                  key={chip.id}
                  id={chip.id}
                  label={chip.label}
                  count={counts[chip.id]}
                  active={active === chip.id}
                  onSelect={setFilter}
                />
              ))}
            </div>
          )}

          <div className="prog-grid mylearning__grid">
            {shown.map((item) => (
              <LibraryCard key={item.id} item={item} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function FilterChip({
  id,
  label,
  count,
  active,
  onSelect,
}: {
  id: Filter;
  label: string;
  count: number;
  active: boolean;
  onSelect: (id: Filter) => void;
}) {
  return (
    <button
      type="button"
      className={`mylearning__chip${active ? ' is-active' : ''}`}
      onClick={() => onSelect(id)}
      aria-pressed={active}
    >
      {label}
      <span className="count">{count}</span>
    </button>
  );
}

/* ================================================================== the card ====== */

/**
 * THE card. One component, both entities.
 *
 * `data-ml-type` and `data-ml-state` are the prototype's own attributes, kept because they
 * say on the element itself what the chip row is filtering by — the same contract its page
 * spec asks production to re-emit.
 *
 * MARKUP, and why it is not one big anchor: the card needs a Continue button, and a button
 * or a second link inside an <a> is invalid and gives a keyboard user two stops for one
 * visible object. So the TITLE is the link, it is stretched over the whole card by
 * `.prog-card__link::after`, and the foot's controls sit above it on the z axis. One card,
 * one primary destination, and every control still reachable in order.
 */
function LibraryCard({ item }: { item: LibraryItem }) {
  return (
    <article className="prog-card" data-ml-type={item.kind} data-ml-state={item.state}>
      <div className="prog-card__art">
        <CardArt item={item} />
        {item.org && (
          <span className="prog-card__org" title={item.org}>
            <Icon name="corporate_fare" />
            {item.org}
          </span>
        )}
        <span className="prog-card__badge">
          <Icon name={item.kind === 'path' ? 'route' : 'menu_book'} />
          {item.kind === 'path' ? text('nav_path_singular', 'Learning path') : 'Course'}
        </span>
      </div>

      <div className="prog-card__body">
        {item.code && <span className="prog-card__track">{item.code}</span>}
        <h3>
          <Link className="prog-card__link" to={item.href}>
            {item.title}
          </Link>
        </h3>
        {item.description && <p className="prog-card__desc">{item.description}</p>}

        <CardProgress item={item} />

        {item.meta.length > 0 && (
          <div className="prog-card__meta">
            {item.meta.map((fact) => (
              <span key={fact.label}>
                <Icon name={fact.icon} />
                {fact.label}
              </span>
            ))}
          </div>
        )}

        <div className="prog-card__foot">
          <span className="prog-card__foot-meta" data-done={item.foot.done || undefined}>
            {item.foot.label && <strong>{item.foot.label}:</strong>}
            {/* `title` because the line ellipsises — a truncated course name still has to be
                readable to whoever wants it, and the card has nowhere else to put it. */}
            <span className="prog-card__foot-value" title={item.foot.value}>
              {item.foot.value}
            </span>
          </span>
          <span className="prog-card__foot-actions">
            {/* Genuinely external: certificates are rendered by the platform, not by us. */}
            {item.certificateHref && (
              <a className="ui-btn" data-variant="ghost" href={item.certificateHref}>
                Certificate
              </a>
            )}
            <Link className="ui-btn" data-variant="primary" to={item.href}>
              {item.action}
            </Link>
          </span>
        </div>
      </div>

      {item.strip && (
        <div className="prog-card__strip" data-tone={item.strip.tone}>
          <Icon name={item.strip.icon} />
          {item.strip.text}
        </div>
      )}
    </article>
  );
}

/**
 * Card artwork, or the brand tile when there is none.
 *
 * `onError` IS NOT OPTIONAL. `bannerImgSrc` is a path the platform hands back whether or not
 * the file behind it survived — a course imported from OLX, or one whose asset was never
 * uploaded, has a perfectly well-formed url and a 404 underneath it. Without this the card
 * drew a broken-image glyph on the gradient, which is the one thing on the dashboard that
 * looks like the page itself is broken. Measured on a live tenant before it was added.
 */
function CardArt({ item }: { item: LibraryItem }) {
  const [failed, setFailed] = useState(false);

  if (item.image && !failed) {
    return (
      <img
        className="prog-card__art-image"
        src={item.image}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="prog-card__art-letter" aria-hidden="true">
      {item.title.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * The progress row and its bar.
 *
 * THREE STATES, and the difference between them is the whole point of this component:
 *
 *   nothing measured   no row at all. Not a 0% bar — we have not asked, or the answer did
 *                      not come, and drawing an empty bar would state a number we do not have.
 *   measured zero      "Not started", no bar. A real answer, said in words: an empty track
 *                      under a heading reads as falling behind, and nobody who has just
 *                      joined has fallen behind.
 *   measured non-zero  the percentage and the bar, green once the thing is finished.
 *
 * ONE EXCEPTION, and it is a real case rather than a tidy-up: a course whose certificate the
 * academy issued without the learner opening every unit measures zero completion and IS
 * finished. Printing "Not started" beside "Certificate issued" is two true statements that
 * read as a contradiction, so a finished or ended thing with nothing measured says nothing
 * here and lets its foot and its strip speak. "Not started" is a claim about what REMAINS,
 * and on those two states nothing does.
 */
function CardProgress({ item }: { item: LibraryItem }) {
  if (item.progress === null) return null;

  const percent = pct(item.progress);
  const done = item.state === 'completed';
  if (percent === 0 && (item.state === 'completed' || item.state === 'archived')) return null;

  return (
    <div className="prog-card__progress">
      <div className="prog-card__progress-row">
        {percent === 0 ? (
          <span>Not started</span>
        ) : (
          <span data-done={done || undefined}>
            <strong>{percent}%</strong> complete
          </span>
        )}
        {item.progressDetail && <span>{item.progressDetail}</span>}
      </div>
      {percent > 0 && (
        <div className="path-bar">
          <div
            className="path-bar__fill"
            data-done={done || undefined}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

function CardSkeleton() {
  return (
    <article className="prog-card prog-card--skeleton">
      <div className="prog-card__art" />
      <div className="prog-card__body">
        <span className="skeleton skeleton--chip" />
        <span className="skeleton skeleton--title" />
        <span className="skeleton skeleton--line" />
      </div>
    </article>
  );
}

/* ============================================================== normalisation ===== */

/** A path the learner is actually in. `enrollment_date` is set only for ACTIVE enrolments. */
const isMine = (view: LearningPathView) => view.state !== 'not_enrolled';

/**
 * One learning path, as a library item.
 *
 * The step count, the completed count and the weighted progress are all `assembleView`'s,
 * measured from the platform's completion data — this function reads them and formats them,
 * and there is deliberately no arithmetic here that could disagree with the path's own page.
 */
/**
 * How many of a PAID path's courses the learner has bought but cannot open yet (spec R6.6,
 * AC-021).
 *
 * Zero for a free path, zero for a path whose price could not be read, and zero for a path
 * whose price is unknown — every one of those is a state where "still being unlocked" would be
 * a guess. A number greater than zero means this learner holds the path, the path costs money,
 * and some of its member courses have no enrolment: fulfilment granted the path and has not
 * finished granting its contents.
 *
 * ONLY `linked` STEPS ARE COUNTED. A step that did not resolve in this academy's catalogue is
 * a tenancy problem the path page already shows as an unlinked row; counting it here would tell
 * the learner to wait for something no payment will ever deliver.
 *
 * Both inputs are already on this page — the path list and the enrolment list — so this costs
 * no request. The pathway price batch is the only addition, and it is one request for the grid.
 */
function pendingGrants(
  view: LearningPathView,
  prices: PricingSeam | undefined,
  enrolled: Set<string>,
): number {
  if (pathwayPriceView(prices, view.path.key).mode !== 'paid') return 0;
  return view.steps.filter((step) => step.linked && !enrolled.has(step.courseKey)).length;
}

function fromPath(view: LearningPathView, pending: number): LibraryItem {
  const { path, state, stepCount, stepsCompleted, progress } = view;
  const next = nextStep(view.steps);

  return {
    id: `path:${path.key}`,
    kind: 'path',
    title: path.title,
    href: pathHref(path.slug),
    image: path.image,
    code: keyNumber(path.key),
    // A path has no publisher of its own in the plugin's model — the academy serving the
    // page is the publisher — so the chip stays off rather than repeating the site name.
    org: null,
    description: path.description || null,
    state: state === 'completed' ? 'completed' : state === 'in_progress' ? 'in_progress' : 'not_started',
    progress,
    progressDetail: `${stepsCompleted} of ${stepCount} ${stepCount === 1 ? 'course' : 'courses'}`,
    // The prototype's meta row is "5 courses · ~32h total · Enrolled". There is no total
    // effort to state — `effort` is free text with no agreed unit, so `totalEffort` refuses
    // to add it up — and the enrolment state is already the badge and the bar, so the row
    // carries the count and the two rules that change how the path behaves.
    meta: [
      { icon: 'route', label: `${stepCount} ${stepCount === 1 ? 'course' : 'courses'}` },
      {
        icon: path.sequential ? 'linear_scale' : 'apps',
        label: path.sequential ? 'In sequence' : 'Any order',
      },
      ...(path.invite_only ? [{ icon: 'lock', label: 'Invitation only' }] : []),
    ],
    foot:
      state === 'completed'
        ? { label: null, value: 'Learning path complete', done: true }
        : next
          ? { label: state === 'in_progress' ? 'Next' : 'Starts with', value: next.title }
          : // Every step unresolvable is a real state — a sequential path whose
            // first course this academy cannot see — and it must not produce a wrong line.
            { label: null, value: 'Open the path to see what comes next' },
    action: state === 'completed' ? 'View path' : state === 'in_progress' ? 'Continue' : 'Start path',
    /**
     * THE ONE STRIP THIS SURFACE GAINS (spec R6.6, AC-021).
     *
     * No tone, so it renders in the neutral treatment rather than the `success` green the
     * certificate strip uses: this is a wait, not an achievement. `schedule` is the icon this
     * app already uses for "we are waiting on something" — the payment-pending note on the
     * course page uses the same one.
     *
     * The card's `href` is the PATH's page and always was, which is what AC-021 asks for: the
     * button cannot drop the learner into a course they have not been granted yet, because it
     * never pointed at a course. So there is nothing to override here — the requirement is
     * already met by the existing destination, and saying so is cheaper than a second `href`
     * that would have to be kept identical.
     */
    ...(pending > 0
      ? {
          strip: {
            icon: 'schedule',
            text: `${pending} of ${stepCount} ${stepCount === 1 ? 'course' : 'courses'} ${
              pending === 1 ? 'is' : 'are'
            } still being unlocked.`,
          },
        }
      : {}),
  };
}

/** One enrolled course, as a library item. */
function fromCourse(entry: LearnerCourse, measured: Map<string, number>): LibraryItem {
  const { course, courseRun, certificate, courseProvider, enrollment } = entry;
  const state = courseState(entry);
  const progress = measured.get(courseRun.courseId);

  return {
    id: `course:${courseRun.courseId}`,
    kind: 'course',
    title: course.courseName,
    // OUR player, never `courseRun.homeUrl`: that is the platform's own courseware path,
    // which matches no route on this origin and rendered a blank page.
    href: learnHref(courseRun.courseId),
    image: course.bannerImgSrc,
    code: course.courseNumber || null,
    org: courseProvider?.name ?? null,
    description: null,
    state,
    // `undefined` means the request has not answered yet; a course that answered zero is a
    // measured zero and says so. The two must not collapse into the same card.
    progress: progress === undefined ? null : progress,
    progressDetail: null,
    // The prototype's course meta is "12 modules · ~16h total · Enrolled". Neither a module
    // count nor an effort is in `/api/learner_home/init`, and fetching an outline per card to
    // manufacture one would be a request per course for a decoration. So the row states the
    // one thing the payload really knows, which is also the prototype's third item.
    meta: [
      certificate.isEarned
        ? { icon: 'workspace_premium', label: 'Certificate earned' }
        : courseRun.isArchived
          ? { icon: 'inventory_2', label: 'Ended' }
          : { icon: 'check_circle', label: 'Enrolled' },
    ],
    foot:
      state === 'completed'
        ? {
            label: null,
            value: certificate.isEarned ? 'Certificate issued' : 'Completed',
            done: true,
          }
        : state === 'archived'
          ? { label: null, value: 'This course has ended' }
          : enrollment.hasStarted
            ? { label: null, value: 'Resume where you left off' }
            : { label: null, value: 'You have not opened this yet' },
    // "Start course" under "Certificate issued" is the dashboard forgetting what it just
    // said. A finished course is still worth opening, so the button stays — it just stops
    // pretending this is the beginning.
    action:
      state === 'completed' ? 'Review course' : enrollment.hasStarted ? 'Continue' : 'Start course',
    certificateHref: certificate.isDownloadable ? certificate.certPreviewUrl : null,
    strip: certificate.isEarned
      ? { tone: 'success' as const, icon: 'workspace_premium', text: 'You earned a certificate' }
      : courseRun.isArchived
        ? { icon: 'inventory_2', text: 'This course has ended' }
        : !courseRun.isStarted
          ? { icon: 'schedule', text: 'This course has not started yet' }
          : undefined,
  };
}

/* =================================================================== helpers ====== */

/**
 * Which shelf a course belongs on.
 *
 * `enrollment.hasStarted` is "has this learner opened it", NOT "has the run begun" — that is
 * `courseRun.isStarted`. Confusing the two puts every future course under "in progress".
 */
function courseState(entry: LearnerCourse): ItemState {
  if (entry.courseRun.isArchived) return 'archived';
  if (entry.certificate.isEarned || entry.gradeData.isPassing) return 'completed';
  return entry.enrollment.hasStarted ? 'in_progress' : 'not_started';
}

/** The same rule as the path detail page's status card, so the two never disagree. */
function nextStep(steps: PathStepView[]): PathStepView | null {
  return (
    steps.find((step) => step.state === 'in_progress' && step.linked) ??
    steps.find((step) => step.state === 'available' && step.linked) ??
    null
  );
}

/**
 * The eyebrow code, out of a pathway key.
 *
 * `path-v1:{org}+{number}+{run}+{group}` — the NUMBER, which is the author's own identifier
 * for the path. Deliberately not the org segment: that is the academy's name, and no tenant
 * value belongs in this app's markup.
 */
function keyNumber(key: string): string | null {
  const parts = key.split('+');
  return parts.length > 1 ? (parts[1] ?? null) : null;
}

/**
 * Unfinished work first, in both lists.
 *
 * A completed thing is an achievement and belongs in the library, but under the ones still
 * asking something of the learner — finished work at the top of the shelf is the dashboard
 * telling somebody they are done when they are not.
 */
const PATH_RANK: Record<LearningPathView['state'], number> = {
  in_progress: 0,
  enrolled: 1,
  completed: 2,
  not_enrolled: 3,
};

function byPathAttention(a: LearningPathView, b: LearningPathView): number {
  return PATH_RANK[a.state] - PATH_RANK[b.state] || a.path.title.localeCompare(b.path.title);
}

const COURSE_RANK: Record<ItemState, number> = {
  in_progress: 0,
  not_started: 1,
  completed: 2,
  archived: 3,
};

function byCourseAttention(a: LibraryItem, b: LibraryItem): number {
  return COURSE_RANK[a.state] - COURSE_RANK[b.state] || a.title.localeCompare(b.title);
}
